import jsQR from "jsqr";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import QrScanner from "qr-scanner";

export const SCANNER_FORMATS = [
  "qr_code",
  "data_matrix",
  "aztec",
  "pdf_417",
  "code_128",
  "code_39",
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "itf",
  "codabar",
];

const SCANNER_FORMAT_ALIASES = {
  qr: ["qr_code"],
  qr_code: ["qr_code"],
  datamatrix: ["data_matrix"],
  data_matrix: ["data_matrix"],
  aztec: ["aztec"],
  pdf417: ["pdf_417"],
  pdf_417: ["pdf_417"],
  ean: ["ean_13", "ean_8"],
  ean13: ["ean_13"],
  ean_13: ["ean_13"],
  ean8: ["ean_8"],
  ean_8: ["ean_8"],
  code128: ["code_128"],
  code_128: ["code_128"],
  code39: ["code_39"],
  code_39: ["code_39"],
  upca: ["upc_a"],
  upc_a: ["upc_a"],
  upce: ["upc_e"],
  upc_e: ["upc_e"],
  itf: ["itf"],
  codabar: ["codabar"],
  todos: SCANNER_FORMATS,
  all: SCANNER_FORMATS,
};

export const normalizeScannerFormats = (rawFormats) => {
  if (!rawFormats || String(rawFormats).trim() === "") {
    return SCANNER_FORMATS;
  }

  const rawValues = Array.isArray(rawFormats)
    ? rawFormats
    : String(rawFormats).split(",");
  const formats = rawValues
    .map((format) => String(format).trim().toLowerCase())
    .filter(Boolean)
    .flatMap((format) => SCANNER_FORMAT_ALIASES[format] ?? []);

  return formats.length > 0 ? [...new Set(formats)] : SCANNER_FORMATS;
};

const getCodeValue = (barcode) => barcode?.rawValue || barcode?.displayValue || "";

const ALL_ZXING_FORMATS = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.AZTEC,
  BarcodeFormat.PDF_417,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.ITF,
  BarcodeFormat.CODABAR,
];

const ZXING_FORMAT_BY_NAME = {
  qr_code: BarcodeFormat.QR_CODE,
  data_matrix: BarcodeFormat.DATA_MATRIX,
  aztec: BarcodeFormat.AZTEC,
  pdf_417: BarcodeFormat.PDF_417,
  code_128: BarcodeFormat.CODE_128,
  code_39: BarcodeFormat.CODE_39,
  ean_13: BarcodeFormat.EAN_13,
  ean_8: BarcodeFormat.EAN_8,
  upc_a: BarcodeFormat.UPC_A,
  upc_e: BarcodeFormat.UPC_E,
  itf: BarcodeFormat.ITF,
  codabar: BarcodeFormat.CODABAR,
};

const resolveZxingFormats = (formats) =>
  normalizeScannerFormats(formats)
    .map((format) => ZXING_FORMAT_BY_NAME[format])
    .filter((format) => format !== undefined);

const GS1_FIXED_LENGTH_AIS = {
  "00": 18,
  "01": 14,
  "02": 14,
  "11": 6,
  "12": 6,
  "13": 6,
  "15": 6,
  "16": 6,
  "17": 6,
  "20": 2,
};

const GS1_VARIABLE_LENGTH_AIS = {
  "10": 20,
  "21": 20,
  "22": 29,
  "30": 8,
  "37": 8,
  "240": 30,
  "241": 30,
  "242": 6,
  "250": 30,
  "251": 30,
  "400": 30,
  "401": 30,
  "420": 20,
};

const getGs1AiDefinition = (value, position) => {
  const candidates = [4, 3, 2]
    .map((length) => value.slice(position, position + length))
    .filter(Boolean);

  for (const ai of candidates) {
    if (GS1_FIXED_LENGTH_AIS[ai] !== undefined) {
      return { ai, fixedLength: GS1_FIXED_LENGTH_AIS[ai] };
    }
    if (GS1_VARIABLE_LENGTH_AIS[ai] !== undefined) {
      return { ai, maxLength: GS1_VARIABLE_LENGTH_AIS[ai] };
    }
  }

  return null;
};

const formatGs1DataMatrix = (value) => {
  if (!value || value.startsWith("(")) return value;

  const parts = [];
  let position = 0;
  let separator = "";

  while (position < value.length) {
    if (value.charCodeAt(position) === 29) {
      separator = value[position];
      position += 1;
      continue;
    }

    const definition = getGs1AiDefinition(value, position);
    if (!definition) return value;

    position += definition.ai.length;
    let fieldValue;

    if (definition.fixedLength !== undefined) {
      fieldValue = value.slice(position, position + definition.fixedLength);
      if (fieldValue.length !== definition.fixedLength) return value;
      position += definition.fixedLength;
    } else {
      const separatorPosition = value.indexOf(String.fromCharCode(29), position);
      const fieldEnd =
        separatorPosition === -1
          ? Math.min(position + definition.maxLength, value.length)
          : separatorPosition;
      fieldValue = value.slice(position, fieldEnd);
      position = fieldEnd;
    }

    parts.push(`(${separator}${definition.ai})${fieldValue}`);
    separator = "";
  }

  return parts.join("");
};

const getNormalizedZxingResult = (result) => {
  const value = result?.getText?.();
  if (!value) return "";

  if (result?.getBarcodeFormat?.() !== BarcodeFormat.DATA_MATRIX) {
    return value;
  }

  // Presentar los Application Identifiers como lo hace el lector físico,
  // conservando el separador ASCII 29 dentro del siguiente identificador.
  return formatGs1DataMatrix(value);
};

const BARCODE_FORMATS = [
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.ITF,
  BarcodeFormat.CODABAR,
];

const createZxingReader = (formats) => {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
  hints.set(DecodeHintType.TRY_HARDER, true);

  return new BrowserMultiFormatReader(hints, {
    delayBetweenScanAttempts: 100,
    delayBetweenScanSuccess: 500,
    tryPlayVideoTimeout: 5000,
  });
};

let scannerCanvas = null;

const getCanvas = (width, height) => {
  if (typeof document === "undefined" || !width || !height) {
    return null;
  }

  if (!scannerCanvas) {
    scannerCanvas = document.createElement("canvas");
  }

  if (scannerCanvas.width !== width) scannerCanvas.width = width;
  if (scannerCanvas.height !== height) scannerCanvas.height = height;

  return scannerCanvas;
};

const decodeCanvas = (canvas, context) => {
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsQR(imageData.data, canvas.width, canvas.height, {
    inversionAttempts: "attemptBoth",
  });

  return result?.data?.trim() ?? "";
};

const detectQrWithJsQr = (source, sourceWidth, sourceHeight) => {
  if (!sourceWidth || !sourceHeight) return "";

  // Keep enough QR detail without processing multi-megapixel mobile frames.
  const scale = Math.min(1, 1280 / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = getCanvas(width, height);
  if (!canvas) {
    return "";
  }

  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    return "";
  }

  context.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, width, height);
  let code = decodeCanvas(canvas, context);
  if (code) return code;

  // Enlarge the center area, where the scanner guide asks users to aim.
  const cropRatio = 0.65;
  const cropWidth = Math.round(sourceWidth * cropRatio);
  const cropHeight = Math.round(sourceHeight * cropRatio);
  const cropX = Math.round((sourceWidth - cropWidth) / 2);
  const cropY = Math.round((sourceHeight - cropHeight) / 2);
  context.clearRect(0, 0, width, height);
  context.drawImage(
    source,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    width,
    height,
  );
  code = decodeCanvas(canvas, context);

  return code;
};

const detectCodeWithZxing = (source, sourceWidth, sourceHeight, formats) => {
  const regions = [
    { x: 0, y: 0, width: sourceWidth, height: sourceHeight },
    {
      x: 0,
      y: Math.round(sourceHeight * 0.15),
      width: sourceWidth,
      height: Math.round(sourceHeight * 0.7),
    },
    {
      x: Math.round(sourceWidth * 0.15),
      y: 0,
      width: Math.round(sourceWidth * 0.7),
      height: sourceHeight,
    },
  ];

  for (const region of regions) {
    const scale = Math.min(
      1,
      1600 / Math.max(region.width, region.height),
    );
    const width = Math.max(1, Math.round(region.width * scale));
    const height = Math.max(1, Math.round(region.height * scale));
    const canvas = getCanvas(width, height);
    if (!canvas) continue;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) continue;

    context.drawImage(
      source,
      region.x,
      region.y,
      region.width,
      region.height,
      0,
      0,
      width,
      height,
    );

    try {
      const requestedFormats = resolveZxingFormats(formats);
      const result = createZxingReader(
        requestedFormats.length > 0 ? requestedFormats : ALL_ZXING_FORMATS,
      ).decodeFromCanvas(canvas);
      const value = getNormalizedZxingResult(result);
      if (value) return value;
    } catch (error) {
      // Try the next crop.
    }
  }

  return "";
};

export const crearBarcodeDetector = async (
  supportsBarcodeDetector,
  formats,
) => {
  if (!supportsBarcodeDetector) {
    return null;
  }

  const requestedFormats = normalizeScannerFormats(formats).map((format) =>
    format === "pdf_417" ? "pdf417" : format,
  );
  let formatosCompatibles = requestedFormats;
  if (typeof window.BarcodeDetector.getSupportedFormats === "function") {
    const soportados = await window.BarcodeDetector.getSupportedFormats();
    const compatibles = requestedFormats.filter((formato) =>
      soportados.includes(formato),
    );
    formatosCompatibles = compatibles.length > 0 ? compatibles : soportados;
  }

  return new window.BarcodeDetector({
    formats: formatosCompatibles.length > 0 ? formatosCompatibles : requestedFormats,
  });
};

const REAR_CAMERA_LABEL_PATTERN =
  /back|rear|environment|trase|trasera|posterior|world/i;

const buildVideoConstraints = (deviceId) => ({
  deviceId: deviceId ? { exact: deviceId } : undefined,
  facingMode: deviceId ? undefined : { ideal: "environment" },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
});

export const getPreferredVideoStream = async () => {
  const mediaDevices = navigator.mediaDevices;
  if (!mediaDevices?.getUserMedia) {
    throw new Error("camera_not_supported");
  }

  try {
    return await mediaDevices.getUserMedia({
      video: {
        facingMode: { exact: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
  } catch (error) {
    // Seguimos con alternativas más permisivas.
  }

  try {
    return await mediaDevices.getUserMedia({
      video: buildVideoConstraints(),
      audio: false,
    });
  } catch (error) {
    // Intentamos seleccionar una cámara trasera por deviceId.
  }

  try {
    const provisionalStream = await mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });

    const devices = await mediaDevices.enumerateDevices();
    const rearCamera = devices.find(
      (device) =>
        device.kind === "videoinput" &&
        REAR_CAMERA_LABEL_PATTERN.test(device.label || ""),
    );

    if (!rearCamera?.deviceId) {
      return provisionalStream;
    }

    provisionalStream.getTracks().forEach((track) => track.stop());
    return await mediaDevices.getUserMedia({
      video: buildVideoConstraints(rearCamera.deviceId),
      audio: false,
    });
  } catch (error) {
    return mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });
  }
};

export const improveCameraFocus = async (stream) => {
  const track = stream?.getVideoTracks?.()[0];
  if (!track?.applyConstraints || !track?.getCapabilities) return;

  try {
    const capabilities = track.getCapabilities();
    const advanced = {};

    if (capabilities.focusMode?.includes?.("continuous")) {
      advanced.focusMode = "continuous";
    }
    if (capabilities.exposureMode?.includes?.("continuous")) {
      advanced.exposureMode = "continuous";
    }
    if (capabilities.whiteBalanceMode?.includes?.("continuous")) {
      advanced.whiteBalanceMode = "continuous";
    }

    if (Object.keys(advanced).length > 0) {
      await track.applyConstraints({ advanced: [advanced] });
    }
  } catch (error) {
    // Camera capability constraints are optional and vary by browser.
  }
};

export const startCameraScanner = async (
  motor,
  { stream, video, onDetected, formatos },
) => {
  if (!video || typeof onDetected !== "function") {
    throw new Error("invalid_qr_scanner_configuration");
  }

  if (motor === "multi-format" || motor === "qr-with-barcode") {
    return startQrWithBarcodeScanner({ video, onDetected, formatos });
  }

  if (motor === "qr-scanner") {
    const scanner = new QrScanner(
      video,
      (result) => {
        const value = typeof result === "string" ? result : result?.data;
        if (value) onDetected(value);
      },
      {
        preferredCamera: "environment",
        maxScansPerSecond: 15,
        highlightScanRegion: true,
        highlightCodeOutline: true,
        returnDetailedScanResult: true,
        calculateScanRegion: (sourceVideo) => {
          const size = Math.round(
            Math.min(sourceVideo.videoWidth, sourceVideo.videoHeight) * 0.72,
          );

          return {
            x: Math.round((sourceVideo.videoWidth - size) / 2),
            y: Math.round((sourceVideo.videoHeight - size) / 2),
            width: size,
            height: size,
            downScaledWidth: 600,
            downScaledHeight: 600,
          };
        },
      },
    );

    scanner.setInversionMode("both");
    await scanner.start();

    return {
      stop: () => scanner.destroy(),
      getStream: () => video.srcObject,
      listCameras: () => QrScanner.listCameras(false),
      setCamera: (cameraId) => scanner.setCamera(cameraId),
      hasFlash: () => scanner.hasFlash(),
      toggleFlash: () => scanner.toggleFlash(),
      isFlashOn: () => scanner.isFlashOn(),
    };
  }

  if (motor !== "zxing") {
    throw new Error("invalid_scanner_engine");
  }

  const scannerStream = stream ?? (await getPreferredVideoStream());

  const requestedFormats = resolveZxingFormats(formatos);
  const reader = createZxingReader(
    requestedFormats.length > 0
      ? requestedFormats
      : ALL_ZXING_FORMATS,
  );

  try {
    const controls = await reader.decodeFromStream(
      scannerStream,
      video,
      (result) => {
        const value = getNormalizedZxingResult(result);
        if (value) onDetected(value);
      },
    );

    return {
      ...controls,
      getStream: () => scannerStream,
    };
  } catch (error) {
    scannerStream.getTracks().forEach((track) => track.stop());
    throw error;
  }
};

export const startQrScanner = (stream, video, onDetected, formatos) =>
  startCameraScanner("zxing", { stream, video, onDetected, formatos });

export const startQrWithBarcodeScanner = async ({
  video,
  onDetected,
  formatos,
  secondaryDelay = 2500,
}) => {
  if (!video || typeof onDetected !== "function") {
    throw new Error("invalid_qr_scanner_configuration");
  }

  const availableCameras = await QrScanner.listCameras(true);
  const cameraCandidates = availableCameras.map((camera) => camera.id).reverse();
  if (cameraCandidates.length === 0) {
    cameraCandidates.push("environment");
  }

  let scanner = null;
  for (const cameraId of cameraCandidates) {
    const candidate = new QrScanner(
      video,
      (result) => {
        const value = typeof result === "string" ? result : result?.data;
        if (value) onDetected(value, "qr");
      },
      {
        preferredCamera: cameraId,
        maxScansPerSecond: 15,
        highlightScanRegion: true,
        highlightCodeOutline: true,
        returnDetailedScanResult: true,
        calculateScanRegion: (sourceVideo) => {
          const size = Math.round(
            Math.min(sourceVideo.videoWidth, sourceVideo.videoHeight) * 0.72,
          );

          return {
            x: Math.round((sourceVideo.videoWidth - size) / 2),
            y: Math.round((sourceVideo.videoHeight - size) / 2),
            width: size,
            height: size,
            downScaledWidth: 600,
            downScaledHeight: 600,
          };
        },
      },
    );
    candidate.setInversionMode("both");

    try {
      await candidate.start();
      scanner = candidate;
      break;
    } catch (error) {
      candidate.destroy();
    }
  }

  if (!scanner) {
    throw new Error("camera_not_available");
  }

  const stream = video.srcObject;
  await improveCameraFocus(stream);

  let secondaryControls = null;
  const formatosZxing = normalizeScannerFormats(formatos).filter(
    (formato) => formato !== "qr_code",
  );
  const secondaryTimer = formatosZxing.length
    ? setTimeout(() => {
        try {
          secondaryControls = startZxingVideoScanner(
            video,
            (value, format) => onDetected(value, "alternative", format),
            formatosZxing,
          );
        } catch (error) {
          // El lector QR sigue disponible si el lector alternativo no inicia.
        }
      }, secondaryDelay)
    : null;

  return {
    stop: () => {
      if (secondaryTimer) clearTimeout(secondaryTimer);
      if (secondaryControls?.stop) {
        secondaryControls.stop();
      }
      scanner.destroy();
    },
    getStream: () => video.srcObject,
    listCameras: () => QrScanner.listCameras(false),
    setCamera: (cameraId) => scanner.setCamera(cameraId),
    hasFlash: () => scanner.hasFlash(),
    toggleFlash: () => scanner.toggleFlash(),
    isFlashOn: () => scanner.isFlashOn(),
  };
};

export const startBarcodeScanner = (video, onDetected) => {
  if (!video || typeof onDetected !== "function") {
    throw new Error("invalid_barcode_scanner_configuration");
  }

  const reader = createZxingReader(BARCODE_FORMATS);
  return reader.scan(video, (result) => {
    const value = getNormalizedZxingResult(result);
    if (value) onDetected(value);
  });
};

export function startZxingVideoScanner(video, onDetected, formatos) {
  if (!video || typeof onDetected !== "function") {
    throw new Error("invalid_zxing_scanner_configuration");
  }

  const requestedFormats = resolveZxingFormats(formatos);
  const reader = createZxingReader(
    requestedFormats.length > 0 ? requestedFormats : ALL_ZXING_FORMATS,
  );

  return reader.scan(video, (result) => {
    const value = getNormalizedZxingResult(result);
    if (value) onDetected(value, result?.getBarcodeFormat?.());
  });
}

export const waitForVideoElement = (videoRef, maxFrames = 30) =>
  new Promise((resolve, reject) => {
    let frames = 0;

    const checkVideo = () => {
      if (videoRef.current) {
        resolve(videoRef.current);
        return;
      }

      frames += 1;
      if (frames >= maxFrames) {
        reject(new Error("video_element_not_available"));
        return;
      }

      requestAnimationFrame(checkVideo);
    };

    requestAnimationFrame(checkVideo);
  });

export const detectarCodigoEnImagen = async (
  imageSource,
  detector,
  formats,
) => {
  if (!imageSource) {
    return "";
  }

  if (detector) {
    try {
      const barcodes = await detector.detect(imageSource);
      if (barcodes?.length > 0) {
        return getCodeValue(barcodes[0]);
      }
    } catch (error) {
      // Si falla BarcodeDetector, igual intentamos decodificar QR.
    }
  }

  const width =
    imageSource.width ||
    imageSource.videoWidth ||
    imageSource.naturalWidth ||
    imageSource.clientWidth;
  const height =
    imageSource.height ||
    imageSource.videoHeight ||
    imageSource.naturalHeight ||
    imageSource.clientHeight;

  const zxingCode = detectCodeWithZxing(imageSource, width, height, formats);
  if (zxingCode) return zxingCode;

  return detectQrWithJsQr(imageSource, width, height);
};
